import { createRecipeSlug } from "base/utils";
import { RecipeContext } from "components/recipe/providers/RecipeProvider";
import { useParams } from "react-router-dom";
import loadingLogo from "images/BlogFreeRecipeLogo.png";
import logo from "images/BlogFreeRecipe-drkBkgrd.png";
import PageDivider from "base/PageDivider";
import React, { useEffect, useMemo, useState, useContext } from "react";
import RecipeBadgeDetails from "components/recipe/RecipeBadgeDetails";
import RecipeDirections from "components/recipe/RecipeDirections";
import RecipeIngredientLinkCard from "components/recipe/RecipeIngredientLinkCard";
import RecipeIngredientToolTab from "components/recipe/RecipeIngredientToolTab";
import RecipeKitchenToolLinkCard from "components/recipe/RecipeKitchenToolLinkCard";
import RecipeServingInfo from "components/recipe/RecipeServingInfo";
import RecipeTiming from "components/recipe/RecipeTiming";
import recipeToJsonLd from "searchOptimizations/recipeToJsonLd";

const SITE_URL = "https://www.blogfreerecipe.com";

const RecipeDetails = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRecipesLoaded, setIsRecipesLoaded] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(null);
  const [jsonLd, setJsonLd] = useState(null);
  const [error, setError] = useState(null); // TODO: implement this rather than just catch the error

  /*
   * Supports both:
   *
   * Old:
   * /recipeDetails/:id
   *
   * New:
   * /recipes/:slug
   */
  const { id, slug } = useParams();
  const { recipes, kitchenTools, featureToggles, fetchRecipeById } =
    useContext(RecipeContext);

  const amazonLinkStatus = useMemo(
    () => featureToggles?.find((f) => f.name?.includes("Amazon"))?.isEnabled,
    [featureToggles],
  );

  const mainPicture = useMemo(() => {
    return recipe?.main_picture || recipe?.additional_pictures?.[0] || logo;
  }, [recipe]);
  /*
   * SEO-friendly slug for this recipe.
   */
  const recipeSlug = useMemo(() => {
    return createRecipeSlug(recipe);
  }, [recipe]);

  /*
   * Canonical URL tells search engines which URL
   * should be treated as the primary recipe URL.
   */
  const canonicalUrl = useMemo(() => {
    if (!recipeSlug) return "";

    return `${SITE_URL}/recipes/${recipeSlug}`;
  }, [recipeSlug]);

  /*
   * Create meta description.
   *
   * recipe-specific description if one exists.
   * Otherwise create one from the recipe name.
   */
  const metaDescription = useMemo(() => {
    if (!recipe) {
      return "Find simple recipes, ingredients, directions, and cooking inspiration at Blog Free Recipe.";
    }

    if (recipe.meta_description) {
      return recipe.meta_description;
    }

    if (recipe.description) {
      return recipe.description
        .replace(/<[^>]*>/g, "")
        .trim()
        .slice(0, 155);
    }

    return `Make ${recipe.recipe_name} with this recipe, including ingredients, cooking times, servings, and step-by-step directions.`;
  }, [recipe]);

  const recipeId = useMemo(() => {
    if (id) return id;
    if (!slug || !Array.isArray(recipes)) return null;
    return recipes.find((item) => createRecipeSlug(item) === slug)?.id ?? null;
  }, [id, slug, recipes]);

  useEffect(() => {
    if (!recipeId) {
      // Slug URL, and the recipe list is still loading: keep waiting
      if (slug && !isRecipesLoaded) return;

      setRecipe(null);
      setError("Recipe not found.");
      setIsLoading(false);
      return;
    }

    let ignore = false;
    setIsLoading(true);
    setError(null);

    fetchRecipeById(recipeId)
      .then((data) => {
        if (!ignore) {
          setRecipe(data);
          setIsRecipesLoaded(true);
        }
      })
      .catch((err) => {
        if (!ignore) {
          setRecipe(null);
          setError(err.message);
        }
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [recipeId, slug, isRecipesLoaded, fetchRecipeById]);

  // Set the adjustable serving count when the recipe loads
  useEffect(() => {
    if (recipe?.num_of_servings) {
      setServings(recipe.num_of_servings);
    }
  }, [recipe]);

  // Pinterest script - only run once
  useEffect(() => {
    const existingScript = document.querySelector(
      'script[src*="assets.pinterest.com/js/pinit.js"]',
    );

    if (existingScript) {
      return;
    }
    const script = document.createElement("script");
    script.async = true;
    script.type = "text/javascript";
    script.dataset.pinBuild = "doBuild";
    script.src = "https://assets.pinterest.com/js/pinit.js";
    document.body.appendChild(script);

    /*
     * intentionally do not remove Pinterest script here.
     * Other recipe pages may still use it after React navigation.
     */
  }, []);

  // Initialize Pinterest buttons when recipe loads
  useEffect(() => {
    if (!recipe) return;
    const pinterestWindow = /** @type {any} */ (window);
    if (typeof pinterestWindow.doBuild === "function") {
      pinterestWindow.doBuild();
    }
    if (typeof pinterestWindow.parsePinBtns === "function") {
      const pinContainer = document.getElementById("pin-it-buttons");
      if (pinContainer) {
        pinterestWindow.parsePinBtns(pinContainer);
      }
    }
  }, [recipe]);

  // Clean up extra Pinterest spans
  useEffect(() => {
    if (!recipe) return;
    //removes additional/ unneeded Pinterest elements
    const spans = document.querySelectorAll('[class="Float"] > span');
    if (spans.length > 1) {
      for (let i = 1; i < spans.length; i++) {
        spans[i].remove();
      }
    }
  }, [recipe]);

  //optimize search results
  useEffect(() => {
    if (!recipe) return;
    let recipeCopy = { ...recipe };
    recipeCopy.main_picture = mainPicture;
    const jsonLdLocal = recipeToJsonLd(recipeCopy, SITE_URL);
    setJsonLd(jsonLdLocal);
  }, [recipe, mainPicture]);
  /*
   * Adding recipe-specific SEO metadata to the document head.
   */
  useEffect(() => {
    if (!recipe) return;

    const pageTitle = `${recipe.recipe_name} Recipe | Blog Free Recipe`;

    document.title = pageTitle;

    /*
     * Helper for updating or creating meta tags.
     */
    const setMetaTag = (selector, attribute, attributeValue, content) => {
      let tag = document.head.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attribute, attributeValue);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    /*
     * Standard search description.
     */
    setMetaTag(
      'meta[name="description"]',
      "name",
      "description",
      metaDescription,
    );

    /*
     * Facebook and several other platforms understand these tags.
     */
    setMetaTag('meta[property="og:title"]', "property", "og:title", pageTitle);

    setMetaTag(
      'meta[property="og:description"]',
      "property",
      "og:description",
      metaDescription,
    );

    setMetaTag('meta[property="og:type"]', "property", "og:type", "article");

    setMetaTag(
      'meta[property="og:image"]',
      "property",
      "og:image",
      mainPicture,
    );

    if (canonicalUrl) {
      setMetaTag('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    }

    /*
     * Social sharing metadata.
     */
    setMetaTag(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image",
    );

    setMetaTag(
      'meta[name="twitter:title"]',
      "name",
      "twitter:title",
      pageTitle,
    );

    setMetaTag(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      metaDescription,
    );

    setMetaTag(
      'meta[name="twitter:image"]',
      "name",
      "twitter:image",
      mainPicture,
    );

    /*
     * Canonical URL.
     */
    if (canonicalUrl) {
      let canonical = document.head.querySelector('link[rel="canonical"]');

      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }

      canonical.setAttribute("href", canonicalUrl);
    }
  }, [recipe, metaDescription, mainPicture, canonicalUrl]);

  const onItemNeeded = (e) => {
    let itemLocation = "#" + e.split(" ").pop();
    window.location.href = `${itemLocation}`;
  };

  return (
    <>
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      <div id="top" className="p1 Recipe-Details">
        <div className="Float">
          <a
            className="Hide"
            data-pin-do="buttonBookmark"
            data-pin-tall="true"
            data-pin-round="true"
            href="https://www.pinterest.com/pin/create/button/"
            aria-label={`Save ${recipe?.recipe_name || "recipe"} to Pinterest`}
          >
            <img
              src="https://assets.pinterest.com/images/pidgets/pinit_fg_en_round_red_32.png"
              alt="Save recipe to Pinterest"
            />
          </a>
        </div>

        {isLoading ? (
          <img
            className="loading"
            src={loadingLogo}
            alt="spinning logo"
            id="Logo"
          />
        ) : recipe ? (
          <>
            <div className="Row marg-1 Space-B">
              <h1>{recipe.recipe_name}</h1>
            </div>
            <div className="Justify-Center">
              <img alt="small main" src={mainPicture} className="Large-Logo" />
            </div>
            <RecipeTiming recipe={recipe} />
            <PageDivider />
            <div className="p1">
              <RecipeServingInfo
                recipe={recipe}
                servings={servings}
                setServings={setServings}
              />
              <div className="p1">
                <RecipeBadgeDetails badges={recipe.badges} />
              </div>
            </div>
            <PageDivider />
            <div className="p1">
              <RecipeIngredientToolTab
                recipe={recipe}
                itemNeeded={onItemNeeded}
                servings={servings}
              />
            </div>
            <PageDivider />

            <RecipeDirections recipe={recipe} />
            {amazonLinkStatus && (
              <>
                <PageDivider />
                <section id="Ingredient" className="Linked-Sections">
                  <h4 className="Linked-Titles">Purchase Ingredients</h4>
                  {recipe.ingredients.length > 0 && (
                    <RecipeIngredientLinkCard
                      ingredients={recipe.ingredients}
                    />
                  )}
                  <h6 className="Disclaimer-Text">
                    Please note: the ingredients in this section are
                    suggestions, and may not be the exact product used by the
                    recipe's author.
                  </h6>
                </section>
                <PageDivider />
                <section id="Tool" className="Linked-Sections">
                  <h4 className="Linked-Titles">Purchase Kitchen Tools</h4>
                  {kitchenTools?.length > 0 &&
                    recipe.kitchen_tools?.length > 0 && (
                      <RecipeKitchenToolLinkCard
                        kitchenTool={recipe.kitchen_tools}
                      />
                    )}
                  <h6 className="Disclaimer-Text">
                    Please note: the kitchen tools in this section are
                    suggestions, and may not be the exact product used by the
                    recipe's author.
                  </h6>
                </section>
              </>
            )}
            <section>
              {recipe?.additional_pictures?.map((p, i) => {
                return (
                  <div key={"additionalPic" + i}>
                    <img
                      src={p}
                      alt={"Additional" + recipe.recipe_name + "picture"}
                      className="Hidden"
                    />
                  </div>
                );
              })}
            </section>
            <a href="#top" className="Row marg-1 Justify-Center Green">
              <h6 className="Small-Font">Top of Recipe</h6>
            </a>
          </>
        ) : (
          <div className="Justify-Center">
            <h1>Recipe Not Found</h1>
          </div>
        )}
      </div>
    </>
  );
};

export default RecipeDetails;
